import { useGetPage } from 'hooks/useGetPage';
import { pageIds } from 'utilities/constants';
import { useUploadFileApi } from 'hooks/useUploadFileApi';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { FileItemType } from '../complete-my-visit-summary/FileItem';
import { useCallback, useMemo, useState } from 'react';
import {
  createAcceptPolicy,
  formatFileSize,
  formatFileType,
  mapApiFileType,
  mapApiFileTypeToFileType,
  mapFileTypeToApiFileType,
} from 'utilities/fileHelpers';
import {
  ContentType,
  GetAppointmentOutput,
  Hasura_Dnavisit_Test_Statuses_Enum,
  useAppointmentThirdPartySubscription,
  useGetAppointmentSummaryQuery,
  useGetDiagnosticReportsByParticipantsQuery,
} from 'graphql/generated/hasura';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuth } from '../../../../auth/context/AuthContext';
import { Roles } from '../../../../firebase/interfaces';

const maxSizeFile = 50 * 1024 * 1024;
const MAX_FILES = 10;

export const allowedTypes = createAcceptPolicy([
  ContentType.Pdf,
  ContentType.Docx,
  ContentType.Doc,
  ContentType.Xls,
  ContentType.Xlsx,
  ContentType.Gif,
  ContentType.Jpeg,
  ContentType.Jpg,
  ContentType.Heic,
  ContentType.Heif,
  ContentType.Webp,
  ContentType.Png,
]);

export function isJsonString(str: string) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}
export type GetAppointmentStatuses = Omit<
  GetAppointmentOutput,
  'recommendations'
>;

export const useSummaryForm = (appointment?: GetAppointmentStatuses | null) => {
  const { user } = useAuth();
  const { data: locale } = useGetPage({
    locale: 'en',
    pageId: pageIds.COMPLETE_MY_VISIT_SUMMARY_MODAL,
  });

  const { uploadEncounterFile: uploadFile, isUploading: fileLoading } =
    useUploadFileApi();

  const { data: tests } = useGetDiagnosticReportsByParticipantsQuery({
    skip:
      !appointment?.codexProviderId ||
      !appointment?.codexPatientId ||
      user?.role !== Roles.PROVIDER,
    fetchPolicy: 'cache-and-network',
    variables: {
      providerCodexId: appointment?.codexProviderId as string,
      patientCodexId: appointment?.codexPatientId as string,
      state: [
        Hasura_Dnavisit_Test_Statuses_Enum.AppointmentScheduled,
        Hasura_Dnavisit_Test_Statuses_Enum.ResultsWithProvider,
      ],
    },
  });

  const { refetch: getSummaryData } = useGetAppointmentSummaryQuery({
    skip: !appointment?.appointmentCodexId,
    variables: {
      appointmentId: appointment?.appointmentCodexId ?? '',
    },
  });

  const schemas = yup.object({
    summaryDiagnosis: yup.string().trim().required('This field is required'),
    followupRecommendations: yup
      .string()
      .trim()
      .required('This field is required'),
    appointmentNotes: yup.string().trim(),
    aiNotes: yup.string().trim(),
    appointmentTranscriptions: yup.string().trim(),
    diagnosticReports: yup.array(),
    files: yup.array(),
  });

  const diagnosticReports = useMemo(() => {
    const appointmentTests = appointment?.diagnosticReports ?? [];
    const appointmentTestIds = appointmentTests.map(
      ({ diagnosticReportId }) => diagnosticReportId,
    );
    const pendingTests =
      tests?.getDiagnosticReportsByParticipants?.diagnosticReports ?? [];
    const viewTests = pendingTests.filter(
      ({ diagnosticReportId }) =>
        !appointmentTestIds.includes(diagnosticReportId),
    );
    if (!appointmentTests.length) {
      return viewTests.map((el) => ({ ...el, isCheckbox: true }));
    }
    return [
      ...appointmentTests.map((el) => ({
        ...el,
        isCheckbox: true,
      })),
      ...viewTests.map((el) => ({ ...el, isCheckbox: false })),
    ];
  }, [tests, appointment]) as never[];

  const parseNablaNotes = (nabla?: string): string => {
    if (!nabla) return '';
    if (!isJsonString(nabla)) {
      return nabla;
    }
    const {
      note: { sections = [] },
    } = JSON.parse(String(nabla));
    return sections
      .map(
        (section: Record<string, string>) =>
          `${section?.title}:\n${section?.text}`,
      )
      .join('\n\n');
  };

  const methods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(schemas),
    values: {
      diagnosticReports: diagnosticReports as never[],
      appointmentTranscriptions:
        appointment?.encounterNotes?.transcription ?? '',
      files: (appointment?.encounterNotes?.files || []).map((item) => {
        return {
          name: item.title,
          url: item.url,
          size: formatFileSize(item.size as number),
          type: item.contentType ? mapApiFileType[item.contentType] : '',
          fileId: item.fileId,
          originalFileType: item.contentType
            ? mapApiFileTypeToFileType[item.contentType]
            : '',
        };
      }) as FileItemType[],
      summaryDiagnosis: appointment?.encounterNotes?.providerSummary || '',
      aiNotes:
        parseNablaNotes(appointment?.encounterNotes?.systemGenerated) ?? '',
      appointmentNotes: appointment?.encounterNotes?.providerNotes || '',
      followupRecommendations:
        appointment?.encounterNotes?.followupRecommendations || '',
    },
  });

  const { data } = useAppointmentThirdPartySubscription({
    skip: !appointment?.appointmentCodexId,
    variables: {
      appointmentId: appointment?.appointmentCodexId,
    },
    onSubscriptionData: ({ subscriptionData }) => {
      if (subscriptionData.data?.appointments?.[0]?.is_transcription_ready) {
        getSummaryData()
          .then((data) => {
            const summary = data?.data?.getAppointmentSummary;
            methods.setValue(
              'appointmentTranscriptions',
              summary?.transcription ?? '',
            );
            methods.setValue('aiNotes', summary?.summary ?? '');
          })
          .catch(console.error);
      }
    },
  });

  const hasPolingLoadData = useMemo(
    () => !!data?.appointments?.[0]?.is_transcription_ready,
    [data],
  );

  const [fileError, setFileError] = useState<string | null>(null);

  const handleImageUpload = useCallback(
    async (files: File[]) => {
      const file = files[0];
      if (!file || !Object.keys(allowedTypes).includes(file.type as string)) {
        setFileError(locale?.formatFileError);
        return;
      }

      if (file.size > maxSizeFile) {
        setFileError(locale?.sizeFileError);
        return;
      }

      setFileError(null);

      const result = await uploadFile(appointment?.encounterId ?? '', file);

      const url = result?.file?.url;
      const mediaId = result?.file?.fileId;

      if (!url || !mediaId) return;

      const newFile = {
        url,
        fileId: mediaId,
        name: file.name,
        originalFileType: file.type,
        type: formatFileType(file.type),
        size: formatFileSize(file.size),
      };
      methods.setValue(
        'files',
        [...(methods.getValues().files || []), newFile as FileItemType],
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    },
    [appointment, locale, methods, uploadFile],
  );

  const handleDeleteFile = (index: number) => {
    const files = (methods.getValues().files || []).filter(
      (f, currentIndex) => currentIndex !== index,
    );
    methods.setValue('files', files as FileItemType[], {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const isFileLimitOver = (methods.getValues().files || []).length >= MAX_FILES;

  const mapFilesForApi = (files: FileItemType[]) => {
    return files.map((file: FileItemType) => ({
      contentType: (file.originalFileType
        ? mapFileTypeToApiFileType[file.originalFileType]
        : ContentType.Pdf) as ContentType,
      fileId: file.fileId as string,
      title: file.name as string,
    }));
  };

  return {
    fileError,
    handleImageUpload,
    handleDeleteFile,
    isFileLimitOver,
    getSummaryData,
    fileLoading,
    mapFilesForApi,
    hasPolingLoadData,
    methods,
  };
};
